import { useState } from 'react';
import { getErrorMessage } from '@/lib/platform';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Chip } from '@/components/ui/chip';

// TipModal collects a post-order tip for the driver (TRV10 2.1.0 — appended to the
// order quote as BUYER_ADDITIONAL_AMOUNT). Rendered as a bottom sheet.
const PRESETS = ['10', '20', '30', '50'];

export function TipModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (amount: string) => Promise<void>;
}) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const valid = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0;

  async function submit() {
    if (!valid) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(amount);
    } catch (e) {
      setError(getErrorMessage(e));
      setSubmitting(false);
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Tip your driver"
      footer={
        <div className="wb-row wb-gap-8" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={submitting || !valid}>
            {submitting ? 'Sending…' : 'Add tip'}
          </Button>
        </div>
      }
    >
      <div className="wb-col wb-gap-12">
        <p className="wb-sub">A tip is added to your fare and paid to the driver.</p>
        <div className="wb-row wb-wrap wb-gap-8">
          {PRESETS.map((p) => (
            <Chip key={p} active={amount === p} onClick={() => setAmount(p)}>
              ₹{p}
            </Chip>
          ))}
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
          placeholder="Custom amount"
          className="wb-field"
        />
        {error && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}
      </div>
    </Sheet>
  );
}
